import React, { useEffect, useState } from 'react';
import {
  getUsageStats,
  calculateCosts,
  subscribeToUsage,
  resetSession,
  resetAllStats,
  getSessionRate,
  getSessionDuration,
  UsageStats,
} from '../services/firebaseUsageTracker';

type Props = {
  expanded?: boolean;
};

export const FirebaseUsageTracker: React.FC<Props> = ({ expanded = false }) => {
  const [stats, setStats] = useState<UsageStats>(getUsageStats);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [showConfirmReset, setShowConfirmReset] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToUsage(() => {
      setStats(getUsageStats());
    });
    return unsubscribe;
  }, []);

  const sessionCosts = calculateCosts('session');
  const todayCosts = calculateCosts('today');
  const allTimeCosts = calculateCosts('allTime');
  const rates = getSessionRate();
  const duration = getSessionDuration();

  const formatCost = (cost: number) => {
    if (cost < 0.0001) return '$0.0000';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const formatNumber = (n: number) => n.toLocaleString();

  const handleResetAll = () => {
    resetAllStats();
    setShowConfirmReset(false);
  };

  return (
    <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">🔥</span>
          <h3 className="text-lg font-semibold">Firebase Usage Tracker</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Session: {duration}</span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Session Reads</div>
          <div className="text-lg font-bold text-blue-400">{formatNumber(stats.session.reads)}</div>
          <div className="text-xs text-gray-500">{rates.readsPerMin}/min</div>
        </div>
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Session Writes</div>
          <div className="text-lg font-bold text-emerald-400">{formatNumber(stats.session.writes)}</div>
          <div className="text-xs text-gray-500">{rates.writesPerMin}/min</div>
        </div>
        <div className="bg-gray-900/60 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Session Cost</div>
          <div className="text-lg font-bold text-yellow-400">{formatCost(sessionCosts.total)}</div>
          <div className="text-xs text-gray-500">estimated</div>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Today's Stats */}
          <div className="border-t border-gray-700 pt-3 mt-3">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Today ({stats.today.date})</h4>
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div>
                <div className="text-gray-400">Reads</div>
                <div className="font-bold text-blue-400">{formatNumber(stats.today.reads)}</div>
              </div>
              <div>
                <div className="text-gray-400">Writes</div>
                <div className="font-bold text-emerald-400">{formatNumber(stats.today.writes)}</div>
              </div>
              <div>
                <div className="text-gray-400">Deletes</div>
                <div className="font-bold text-red-400">{formatNumber(stats.today.deletes)}</div>
              </div>
              <div>
                <div className="text-gray-400">Cost</div>
                <div className="font-bold text-yellow-400">{formatCost(todayCosts.total)}</div>
              </div>
            </div>
          </div>

          {/* All Time Stats */}
          <div className="border-t border-gray-700 pt-3 mt-3">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              All Time (since {new Date(stats.allTime.firstTracked).toLocaleDateString()})
            </h4>
            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <div>
                <div className="text-gray-400">Reads</div>
                <div className="font-bold text-blue-400">{formatNumber(stats.allTime.reads)}</div>
              </div>
              <div>
                <div className="text-gray-400">Writes</div>
                <div className="font-bold text-emerald-400">{formatNumber(stats.allTime.writes)}</div>
              </div>
              <div>
                <div className="text-gray-400">Deletes</div>
                <div className="font-bold text-red-400">{formatNumber(stats.allTime.deletes)}</div>
              </div>
              <div>
                <div className="text-gray-400">Cost</div>
                <div className="font-bold text-yellow-400">{formatCost(allTimeCosts.total)}</div>
              </div>
            </div>
          </div>

          {/* Free Tier Status */}
          <div className="border-t border-gray-700 pt-3 mt-3">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Free Tier Usage (Today)</h4>
            <div className="space-y-2">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Writes ({formatNumber(stats.today.writes)} / 20,000)</span>
                  <span className="text-gray-400">{((stats.today.writes / 20000) * 100).toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      stats.today.writes / 20000 > 0.8 ? 'bg-red-500' : 
                      stats.today.writes / 20000 > 0.5 ? 'bg-yellow-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, (stats.today.writes / 20000) * 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-400">Reads ({formatNumber(stats.today.reads)} / 50,000)</span>
                  <span className="text-gray-400">{((stats.today.reads / 50000) * 100).toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      stats.today.reads / 50000 > 0.8 ? 'bg-red-500' : 
                      stats.today.reads / 50000 > 0.5 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(100, (stats.today.reads / 50000) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Recent Operations */}
          <div className="border-t border-gray-700 pt-3 mt-3">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Recent Operations</h4>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {stats.recentOperations.slice(-10).reverse().map((op, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-gray-900/40 rounded px-2 py-1">
                  <div className="flex items-center gap-2">
                    <span className={
                      op.type === 'read' ? 'text-blue-400' : 
                      op.type === 'write' ? 'text-emerald-400' : 'text-red-400'
                    }>
                      {op.type.toUpperCase()}
                    </span>
                    <span className="text-gray-300">{op.collection}</span>
                    {op.docCount > 1 && <span className="text-gray-500">×{op.docCount}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{op.source}</span>
                    <span className="text-gray-600">
                      {new Date(op.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
              {stats.recentOperations.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-2">No operations tracked yet</div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="border-t border-gray-700 pt-3 mt-3 flex gap-2">
            <button
              onClick={resetSession}
              className="text-xs px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600"
            >
              Reset Session
            </button>
            {showConfirmReset ? (
              <div className="flex gap-2">
                <button
                  onClick={handleResetAll}
                  className="text-xs px-3 py-1.5 rounded bg-red-700 hover:bg-red-600"
                >
                  Confirm Reset All
                </button>
                <button
                  onClick={() => setShowConfirmReset(false)}
                  className="text-xs px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirmReset(true)}
                className="text-xs px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600"
              >
                Reset All Stats
              </button>
            )}
          </div>

          {/* Disclaimer */}
          <div className="mt-3 text-xs text-gray-500">
            Note: This tracks client-side operations only. Check Firebase Console for accurate billing.
            Costs are estimates based on current Firestore pricing.
          </div>
        </>
      )}
    </div>
  );
};

